import { Heading, VStack } from '@chakra-ui/react';
import { usePropertiesModel } from '@common/models/PropertiesModel';
import { FontFamily } from '@common/theming/Fonts';
import { RegulatoryRuleType } from '@services/appsync/types';
import React from 'react';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';

export const Disclosures: React.FC = () => {
  const { property } = usePropertiesModel();
  const regulatoryType = property?.regulatory_rule_type;

  const renderRegDDisclosure = (
    <>
      <TextBlock>
        This Offering is a Reg D 506(b) private placement. Concreit does not provide refunds on completed investments.
        Please review all offering documents and subscription details for more insights.
      </TextBlock>

      <TextBlock>
        This Website is a summary of information and you should refer to the Final Investment Memorandum for additional
        details and disclaimers. This presentation and the information contained herein (including the terms of the
        investment) are subject to completion or amendment without notice. Under no circumstances shall this Website
        constitute an offer or invitation to make an investment, in any form. It has been prepared for the sole purpose
        of obtaining indications of interest about a potential investment, which will only be made upon the basis of the
        negotiations and executions of full investment documentation.
      </TextBlock>
    </>
  );

  const renderRegADisclosure = (
    <>
      <TextBlock>
        Securities-related activity for our Home Shares™ related offerings are conducted through Cultivate Capital
        Group, LLC, a registered broker-dealer and member of FINRA/SIPC, located at 859 Madison Avenue, Bridgewater, NJ
        08807.
      </TextBlock>
      <TextBlock>
        Concreit Series LLC is conducting public offerings pursuant to Regulation A, as amended, through the Site.
        Concreit does not provide refunds on completed investments. Please review all offering documents and
        subscription details for more insights. When you invest through Concreit's Home Shares™, you're investing in a
        specific series, not directly in Concreit or a portfolio. It's important to look closely at the risks mentioned
        in the details of each investment opportunity. We regularly introduce new investment series under Regulation A.
        For any series not yet approved, we're not asking for money yet, and if sent, we won't accept it. We can't
        finalize any investment in a series until the SEC (Securities and Exchange Commission) approves our filing for
        that series. We have the right to cancel or change any investment offer at any time before we officially accept
        it, after the SEC gives the green light. Just showing interest doesn't mean you're committed to anything.
      </TextBlock>
      <TextBlock>
        The investment materials presented here provide a brief overview of the investment opportunities' aims and key
        business terms. These overviews are designed solely for informational use and do not claim to provide a
        comprehensive picture. For a more in-depth understanding, each summary should be supplemented by the detailed
        discussions found in the respective offering materials. Investors should verify any information they consider
        important before making an investment.
      </TextBlock>
      <TextBlock>
        This App & Website provides a summary of information and you should refer to the Final Offering Circular for
        additional details and disclaimers. This presentation and the information contained herein (including the terms
        of the investment) are subject to completion or amendment without notice. Under no circumstances shall this App
        or Website constitute an offer or invitation to make an investment, in any form. It has been prepared for the
        sole purpose of obtaining indications of interest about a potential investment, which will only be made upon the
        basis of the negotiations and executions of full investment documentation. This is not an offer of an investment
        opportunity in any jurisdiction where it is prohibited or where a pre-filing or other action is required.
      </TextBlock>
    </>
  );

  return (
    <VStack gap="20px" alignItems={['center', 'flex-start']} fontSize={'14px'}>
      <Heading
        letterSpacing={-0.8}
        fontFamily={FontFamily.HELVETICA_NOW_BOLD}
        fontWeight="normal"
        fontSize={['28px', '36px']}>
        Important Disclosures
      </Heading>

      {/* General Disclosures - Always show */}
      <TextBlock>
        Investments offered on the Concreit Platform are speculative and involve substantial risks to consider before
        investing, which are outlined in the respective offering materials. Those risks include, but are not limited to,
        illiquidity, lack of diversification, complete loss of capital, limited operating history, risk of asset damage
        or theft and lack of voting rights. The investments are sensitive to factors such as changes to real estate
        values and property taxes, interest rates, cash flow, supply and demand, and the management skill and credit
        worthiness of the issuer.
      </TextBlock>

      {/* Regulatory-specific disclosures */}
      {[RegulatoryRuleType.REG_D_506B, RegulatoryRuleType.REG_D_506C].includes(regulatoryType) && renderRegDDisclosure}
      {[RegulatoryRuleType.REG_A_PLUS_TIER1, RegulatoryRuleType.REG_A_PLUS_TIER2].includes(regulatoryType) &&
        renderRegADisclosure}

      {/* Additional general disclosures */}
      <TextBlock>
        An active, consistent trade market for any Concreit Home interests isn't guaranteed. If no such market arises or
        continues, reselling your interests could become challenging, potentially at any price. Even with an active
        market, the trading price could drop below your purchase price.
      </TextBlock>

      <TextBlock>
        Forward-Looking Statements: This information contains forward-looking statements that are subject to risks and
        uncertainties that could cause actual results to differ materially. Words such as "expects," "believes,"
        "anticipates," and similar expressions identify forward-looking statements. We undertake no obligation to update
        these statements, and investors should not place undue reliance on them.
      </TextBlock>

      <TextBlock>
        <sup>†</sup> Equity Appreciation: Our estimated equity appreciation is calculated by considering the current
        national average and the historical average appreciation rate in the property's geographical area.
      </TextBlock>

      <TextBlock>
        <sup>‡</sup> Hypothetical 1-year Return: Our hypothetical 1-year return is an estimate that combines projected
        equity appreciation and rental income, and represents an average annual return expected if the property is sold
        after a five to seven-year holding period.
      </TextBlock>
    </VStack>
  );
};
